import { inject, Injectable, signal, Signal } from '@angular/core';
import { Observable, take } from 'rxjs';
import { LoggerService } from '@lx/shared/logger';
import { GoogleAnalyticsService } from '@lx/shared/google-analytics';

@Injectable({ providedIn: 'root' })
export class CopyQuoteSelectionsService {
  private readonly logger = inject(LoggerService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  private readonly copiedState = signal(false);
  readonly copied: Signal<boolean> = this.copiedState.asReadonly();

  /**
   * Handles copy quote selections action.
   * Generates deeplink using provided generator, copies to clipboard, and manages copied state.
   *
   * @param deeplinkGenerator$ Observable that emits the deeplink URL
   * @param gaEventName Optional GA event name to track
   */
  handleCopyRequested(deeplinkGenerator$: Observable<string | null>, gaEventName?: string): void {
    if (gaEventName) {
      this.googleAnalyticsService.trackClickEvent(gaEventName);
    }

    deeplinkGenerator$.pipe(take(1)).subscribe(async (url) => {
      if (!url) {
        this.logger.warn?.('Failed to generate deeplink');
        return;
      }

      try {
        await navigator.clipboard.writeText(url);
        this.setCopiedState(true);
        this.resetCopiedStateAfterDelay();
      } catch (err) {
        this.logger.error('Failed to copy deeplink to clipboard', err);
      }
    });
  }

  /**
   * Sets the copied state signal
   * @param value New copied state
   */
  setCopiedState(value: boolean): void {
    this.copiedState.set(value);
  }

  /**
   * Resets the copied state after a delay
   */
  private resetCopiedStateAfterDelay(): void {
    setTimeout(() => {
      this.setCopiedState(false);
    }, 2000);
  }
}
